import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import dayjs from '@dcp/shared/utils/dayjs'
import {
  KpiCards,
  BalanceImportFilters,
  BalanceImportTabs,
  BalanceImportTable,
} from '@/components/banking'
import {
  useBankingKpi,
  useBalanceImportStatus,
  useFacilities,
} from '@/hooks/useBanking'
import { BALANCE_IMPORT_PAGE_SIZE } from '@/types/banking'
import type { BankingFilters, BalanceImportTab } from '@/types/banking'

/**
 * Balance Import page
 * Displays balance import status with filters, KPI cards, and tabbed interface
 */
export function BalanceImportPage() {
  const [filters, setFilters] = useState<BankingFilters>({
    endDate: dayjs().format('YYYY-MM-DD'),
    facilityIds: undefined,
  })
  // Default to the "Bank balance update" tab (DCP-120/DCP-267): the inline-edit
  // table, which is also the first tab.
  const [activeTab, setActiveTab] = useState<BalanceImportTab>('balance-manual-update')

  // 1-indexed page. Reset to 1 whenever the facility filter changes so a
  // user who was on page 3 of "all accounts" doesn't end up past the end
  // of the filtered result set.
  const [page, setPage] = useState(1)
  useEffect(() => {
    setPage(1)
  }, [filters.facilityIds])

  // Fetch data
  const { data: kpi, isLoading: kpiLoading } = useBankingKpi(filters)
  const { data: balanceStatus, isLoading: statusLoading } = useBalanceImportStatus({
    page,
    pageSize: BALANCE_IMPORT_PAGE_SIZE,
    facilityIds: filters.facilityIds,
  })
  const { data: facilities, isLoading: facilitiesLoading } = useFacilities()

  const isLoading = kpiLoading || statusLoading || facilitiesLoading

  if (isLoading && !kpi) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      {facilities && (
        <BalanceImportFilters
          filters={filters}
          onFiltersChange={setFilters}
          facilities={facilities}
        />
      )}

      {/* Aggregated balances (DCP-297): rendered unconditionally and told about
          the loading state, like every other Banking tab — otherwise the row
          pops into existence when the query resolves and shifts the page. */}
      <KpiCards kpi={kpi} loading={kpiLoading} />

      {/* Tabs with Balance Import Table */}
      <BalanceImportTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        facilityIds={filters.facilityIds}
      >
        <BalanceImportTable
          data={balanceStatus?.items ?? []}
          totalCount={balanceStatus?.total ?? 0}
          page={page}
          pageSize={BALANCE_IMPORT_PAGE_SIZE}
          onPageChange={setPage}
        />
      </BalanceImportTabs>
    </div>
  )
}